import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { getCasinoGameDetails, getMatchExposure } from "@/services/casinoApi";
import CasinoHeading from "../components/common/CasinoHeading";
import VideoBox from "../components/common/VideoBox";
import BetTablePoker20 from "../components/poker20/BetTablePoker20";
import ResultPoker20 from "../components/poker20/ResultPoker20";
import RightSection from "../components/RightSection/RightSection";
import styles from "./Poker20.module.css";

function extractCasinoGame(url) {
  const match = url.match(/\/lord-games\/([^/?#]+)/);
  return match ? match[1] : null;
}

// Helper function to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
};

// Helper function to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  return [...new Set(keys.filter(k => k))];
};

export default function Poker20() {
  const [gameData, setGameData] = useState([]);
  const [activeTab, setActiveTab] = useState("game");
  const [iframeSrc, setIframeSrc] = useState("");
  const { pathname } = useLocation();
  const [subData, setSubData] = useState([]);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);

  useEffect(() => {
    const name = extractCasinoGame(pathname) || "poker20";
    setIframeSrc(`https://casino-stream.softgamingapi.com/casino-tv?id=${name}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
        const data = response?.data?.data?.sub || [];
        setSubData(data);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    // Start API calls after 5 seconds delay
    let intervalId = null;
    const initialTimeout = setTimeout(() => {
      fetchGameData();
      intervalId = setInterval(fetchGameData, 1500);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pathname]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id;
      const matchId = gameData?.data?.data?.mid?.toString();
      
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || item.nat || "";
            const exposureAmount = parseFloat(item.exposure_amount || item.exposure || 0) || 0;
            if (teamName && exposureAmount !== 0) {
              // Create keys for the team name as-is
              const keys = createExposureKeys(teamName);
              keys.forEach(key => {
                if (key) exposureMap[key] = exposureAmount;
              });
              
              // Also create keys with Player A/B format
              const betTypes = ["Winner", "One Pair", "Two Pair", "Three of a Kind", "Straight", "Flush", "Full House", "Four of a Kind", "Straight Flush"];
              betTypes.forEach(betType => {
                const keyWithType = `${betType} Player A`;
                const keyWithTypeB = `${betType} Player B`;
                if (teamName.includes(betType) && teamName.includes("A")) {
                  exposureMap[keyWithType] = exposureAmount;
                }
                if (teamName.includes(betType) && teamName.includes("B")) {
                  exposureMap[keyWithTypeB] = exposureAmount;
                }
              });
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [gameData?.data?.data?.mid]);

  useEffect(() => {
    const matchId = gameData?.data?.data?.mid?.toString();
    if (!matchId) return;

    // Start exposure fetch after 5 seconds delay
    const timeoutId = setTimeout(() => {
      fetchExposure();
    }, 5000);

    const intervalId = setInterval(() => {
      fetchExposure();
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [gameData?.data?.data?.mid, fetchExposure]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleBetClick = (value, selection, betData, playerName) => {
    // Betting functionality can be added later (admin view - no bet login)
    console.log("Bet clicked:", { value, selection, betData, playerName });
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.sectionA}>
          <CasinoHeading
            name="20-20 POKER"
            roundId={gameData?.data?.data?.mid || "157250124095658"}
            placedBetCount={myBets.length}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {activeTab === "game" && (
            <>
              <VideoBox 
                gameType="poker20"
                resultData={gameData?.data?.data?.card}
                iframeSrc={iframeSrc}
                timerValue={gameData?.data?.data?.lt}
              />

              <div className={styles.tableContainer}>
                <div className={styles.bettable}>
                  <BetTablePoker20
                    data={subData}
                    onBetClick={handleBetClick}
                    exposures={exposures}
                    playerA="A"
                    playerB="B"
                  />
                </div>
              </div>

              <div className={styles.heading}>
                <div>Last Result</div>
                <Link to="/casino/results/poker20">
                  <div className={styles.viewAll}>View All</div>
                </Link>
              </div>

              <ResultPoker20
                data={gameData?.data?.lrs || []}
                gameData={gameData}
              />
            </>
          )}

          {activeTab === "placedBet" && (
            <div className="mt-4 p-4 bg-[rgba(0,0,0,0.4)] rounded">
              <div className="text-white text-center text-xl">Coming Soon</div>
            </div>
          )}
        </div>

        <RightSection
          sectionKey="poker20"
          data={myBets}
          title="Matched"
          viewMoreText="View More"
          onViewMore={() => {
            console.log("View more clicked for poker20");
          }}
        />
      </div>
    </Layout>
  );
}

